import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const POLL_INTERVAL = 2000;
const MAX_POLLS = 15;

export default function BillingSuccess() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", data: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "failed", data: { message: "Missing session id" } });
      return;
    }
    let attempts = 0;
    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      attempts++;
      try {
        const { data } = await api.get(`/billing/checkout/${sessionId}/status`);
        if (data.payment_status === "paid") {
          setState({ phase: "success", data });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "failed", data: { message: "Session expired" } });
          return;
        }
        if (attempts >= MAX_POLLS) {
          setState({ phase: "failed", data: { message: "Timeout — please contact support if you were charged" } });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        setState({ phase: "failed", data: { message: e?.response?.data?.detail || e.message } });
      }
    };
    poll();
    return () => {
      stopped = true;
    };
  }, [sessionId]);

  return (
    <div className="App min-h-screen bg-background">
      <Header variant="app" />
      <main className="relative">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background" />
        <div className="relative mx-auto flex max-w-xl flex-col items-center px-4 py-24 text-center sm:px-6">
          {state.phase === "checking" && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-[#002FA7]" />
              <h1 className="mt-6 font-display text-3xl font-black tracking-tight">{t("pricing.checking")}</h1>
              <p className="mt-2 font-mono text-xs text-muted-foreground">session::{sessionId?.slice(0, 16)}…</p>
            </>
          )}
          {state.phase === "success" && (
            <>
              <CheckCircle2 className="h-14 w-14 text-emerald-500" />
              <h1 className="mt-6 font-display text-3xl font-black tracking-tight">{t("pricing.success")}</h1>
              <p className="mt-2 text-muted-foreground">
                {t("pricing.planLabel")}: <span className="font-mono uppercase">{state.data?.plan_id}</span>
              </p>
              <Button
                onClick={() => navigate("/dashboard")}
                className="mt-8 rounded-sm bg-[#002FA7] hover:bg-[#002FA7]/90 text-white h-11 px-6"
                data-testid="billing-success-back"
              >
                {t("pricing.backToDashboard")}
              </Button>
            </>
          )}
          {state.phase === "failed" && (
            <>
              <XCircle className="h-14 w-14 text-destructive" />
              <h1 className="mt-6 font-display text-3xl font-black tracking-tight">{t("pricing.failed")}</h1>
              <p className="mt-2 font-mono text-xs text-muted-foreground">{state.data?.message}</p>
              <Button
                onClick={() => navigate("/pricing")}
                variant="outline"
                className="mt-8 rounded-sm h-11 px-6"
                data-testid="billing-failed-back"
              >
                {t("common.back")}
              </Button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
